import React, { useState, useRef, useEffect } from 'react';
import { Search, Sparkles, ArrowRight, Brain } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useContextEngine } from '@/hooks/use-context-engine';

interface ContextSearchBarProps {
  onSearch: (query: string) => void;
  placeholder?: string;
  isLoading?: boolean;
  showContextIndicator?: boolean;
}

export const ContextSearchBar: React.FC<ContextSearchBarProps> = ({
  onSearch,
  placeholder = "What's your current state or need?",
  isLoading = false,
  showContextIndicator = true
}) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Connect to context engine hook
  const { 
    processExpression,
    debouncedProcessExpression,
    isSearching: isEngineSearching,
    contextAnalysis,
    sessionId
  } = useContextEngine();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim() && !isLoading && !isEngineSearching) {
      // Process through context engine
      processExpression(query.trim());
      
      // Also call the original onSearch for compatibility
      onSearch(query.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="relative group">
      <div className="relative">
        {/* Background with gradient border effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-accent/20 to-secondary/20 rounded-2xl blur-sm opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-300" />
        
        {/* Main input container */}
        <div className="relative bg-background/80 backdrop-blur-sm border border-border/50 rounded-xl transition-all duration-300 group-hover:border-primary/50 group-focus-within:border-primary/70 group-focus-within:bg-background/90">
          
          {/* Search icon */}
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors">
            {isLoading || isEngineSearching ? (
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            ) : (
              <Brain className="w-5 h-5" />
            )}
          </div>

          {/* Input field */}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              // Trigger debounced search for typing suggestions
              if (e.target.value.length > 3) {
                debouncedProcessExpression(e.target.value);
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isLoading || isEngineSearching}
            className="w-full pl-12 pr-24 py-4 bg-transparent text-lg placeholder:text-muted-foreground/70 focus:outline-none disabled:opacity-50"
          />

          {/* Submit button */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <Button
              type="submit"
              size="sm"
              disabled={!query.trim() || isLoading || isEngineSearching}
              className="rounded-lg px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-primary-foreground disabled:opacity-50 transition-all duration-200 hover:scale-105"
            >
              {isLoading || isEngineSearching ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <ArrowRight className="w-4 h-4 mr-1" />
                  Explore
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Sparkle effects */}
        <div className="absolute -top-1 -left-1 w-3 h-3 bg-primary/40 rounded-full blur-sm opacity-0 group-focus-within:opacity-100 animate-pulse" />
        <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-accent/40 rounded-full blur-sm opacity-0 group-focus-within:opacity-100 animate-pulse delay-150" />
      </div>

      {/* Status indicator */}
      {query && showContextIndicator && (
        <div className="absolute -bottom-8 left-4 flex items-center gap-2 text-xs text-muted-foreground">
          <Sparkles className="w-3 h-3" />
          {sessionId ? (
            <span className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>Conversation active</span>
            </span>
          ) : (
            <span>Enter your state or need</span>
          )}
        </div>
      )}
      
      {/* Context understanding indicator */}
      {contextAnalysis && showContextIndicator && (
        <div className="absolute top-full mt-1 right-4 max-w-xs">
          <div className="bg-black/30 backdrop-blur-sm rounded-md p-2 text-xs text-gray-300 animate-fade-in">
            <div className="flex items-center gap-1.5 text-purple-300 mb-1">
              <Brain className="w-3 h-3" /> 
              <span>Understanding your context</span>
            </div>
            {contextAnalysis.emotionalState && (
              <div className="flex gap-1 flex-wrap mt-1">
                {contextAnalysis.interests?.slice(0, 2).map((interest, i) => (
                  <span key={i} className="px-1.5 py-0.5 bg-purple-900/50 rounded-full text-[10px]">{interest}</span>
                ))}
                <span className="px-1.5 py-0.5 bg-indigo-900/50 rounded-full text-[10px]">{contextAnalysis.emotionalState}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </form>
  );
};